import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowDown, RefreshCcw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pendli-admin-token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
};

const PRESETS = [
  { v: "today", l: "I dag", range: () => ({ date_from: iso(new Date()), date_to: iso(new Date()) }) },
  { v: "7", l: "Siste 7 dager", range: () => ({ date_from: daysAgo(6), date_to: iso(new Date()) }) },
  { v: "30", l: "Siste 30 dager", range: () => ({ date_from: daysAgo(29), date_to: iso(new Date()) }) },
  { v: "all", l: "Alt", range: () => ({ date_from: "", date_to: "" }) },
  { v: "custom", l: "Egendefinert", range: null },
];

const PAGE_LABELS = {
  home: "Forside (/)",
  huseier: "Huseier (/huseier)",
  pendler: "Pendler (/pendler)",
  kontakt: "Kontakt (/kontakt)",
  "om-oss": "Om oss (/om-oss)",
  personvern: "Personvern",
  vilkar: "Vilkår",
  other: "Andre",
};
const JOURNEY_LABELS = { huseier: "Huseier (host)", pendler: "Pendler (commuter)", "(ingen)": "Ikke identifisert" };
const DEVICE_LABELS = { mobile: "Mobil", desktop: "Desktop", tablet: "Tablet", unknown: "Ukjent" };

const Metric = ({ label, value, sub, color = "text-amber", testId }) => (
  <div className="bg-amber-deep border border-amber/20 p-5" data-testid={testId}>
    <div className="text-xs font-bold tracking-widest uppercase text-cream/60 mb-1">{label}</div>
    <div className={`font-display text-3xl md:text-4xl font-extrabold ${color}`} style={{ letterSpacing: "-0.04em" }}>
      {value}
    </div>
    {sub && <div className="text-xs text-cream/50 mt-1">{sub}</div>}
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">{label}</span>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  </label>
);

function Funnel({ funnel }) {
  const max = Math.max(1, ...funnel.map((s) => s.count));
  return (
    <div className="bg-amber-deep/30 border border-amber/15 p-5 md:p-7" data-testid="analytics-funnel">
      <h2 className="font-display text-lg font-bold tracking-tight mb-6">Trakt</h2>
      <div className="space-y-1">
        {funnel.map((s, i) => (
          <div key={s.step}>
            {i > 0 && (
              <div className="flex items-center gap-2 pl-1 py-1 text-xs text-orange font-bold" data-testid={`funnel-rate-${s.step}`}>
                <ArrowDown size={14} /> {s.rate_from_prev}%
              </div>
            )}
            <div className="flex items-center gap-4" data-testid={`funnel-step-${s.step}`}>
              <div className="w-40 md:w-48 shrink-0 text-xs font-bold tracking-widest uppercase text-cream/70 leading-tight">{s.label}</div>
              <div className="flex-1 h-9 bg-amber/10">
                <div className="h-full bg-amber" style={{ width: `${(s.count / max) * 100}%` }} />
              </div>
              <div className="w-16 shrink-0 text-right font-display text-lg font-extrabold text-amber">{s.count}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Table({ title, rows, keyLabel, labels, testId, columns = "full" }) {
  return (
    <div className="border border-amber/15 overflow-x-auto" data-testid={testId}>
      <div className="px-4 py-3 bg-amber-deep/60">
        <h3 className="font-display text-base font-bold tracking-tight">{title}</h3>
      </div>
      <table className="w-full text-sm min-w-[520px]">
        <thead className="text-amber/80 text-xs font-bold tracking-widest uppercase border-b border-amber/15">
          <tr>
            <th className="text-left px-4 py-2.5">{keyLabel}</th>
            <th className="text-right px-4 py-2.5">Besøkende</th>
            {columns === "full" && <th className="text-right px-4 py-2.5">CTA</th>}
            {columns === "full" && <th className="text-right px-4 py-2.5">Startet</th>}
            <th className="text-right px-4 py-2.5">Registreringer</th>
            <th className="text-right px-4 py-2.5">Konv.</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-cream/50">Ingen data i perioden.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-amber/10 hover:bg-amber/5">
              <td className="px-4 py-2.5 text-cream">{(labels && labels[r.key]) || r.key}</td>
              <td className="px-4 py-2.5 text-right text-cream/80">{r.visitors}</td>
              {columns === "full" && <td className="px-4 py-2.5 text-right text-cream/70">{r.cta_clicks}</td>}
              {columns === "full" && <td className="px-4 py-2.5 text-right text-cream/70">{r.registration_starts}</td>}
              <td className="px-4 py-2.5 text-right text-amber font-bold">{r.registrations}</td>
              <td className="px-4 py-2.5 text-right text-cream/70">{r.conversion}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAnalytics({ onUnauthorized }) {
  const [preset, setPreset] = useState("30");
  const [range, setRange] = useState(PRESETS[2].range());
  const [filters, setFilters] = useState({
    source_type: "", utm_source: "", utm_medium: "", utm_campaign: "", device: "", page: "", journey: "",
  });
  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setF = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));

  const choosePreset = (v) => {
    setPreset(v);
    const p = PRESETS.find((x) => x.v === v);
    if (p?.range) setRange(p.range());
  };

  const params = useMemo(() => {
    const p = {};
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [range, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sum, opts] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`, { headers: authHeaders(), params }),
        axios.get(`${API}/admin/analytics/filters`, { headers: authHeaders() }),
      ]);
      setData(sum.data);
      setOptions(opts.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError("Klarte ikke laste analytics.");
    } finally {
      setLoading(false);
    }
  }, [params, onUnauthorized]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const t = data?.totals;
  const r = data?.rates;
  const opt = (label, list) => [{ v: "", l: label }, ...(list || []).map((x) => ({ v: x, l: x }))];

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter" style={{ letterSpacing: "-0.04em" }}>
            Analytics
          </h1>
          <p className="text-sm text-cream/50 mt-2">Førsteparts trafikk- og konverteringsdata · ingen tredjeparts sporing</p>
        </div>
        <button onClick={load} data-testid="analytics-refresh" className="h-9 px-3 border border-amber/30 text-cream/80 hover:text-amber text-xs font-bold tracking-widest uppercase inline-flex items-center gap-1.5">
          <RefreshCcw size={13} /> Oppdater
        </button>
      </div>

      {/* Period */}
      <div className="flex flex-wrap items-end gap-3 mb-6" data-testid="analytics-period">
        {PRESETS.map((p) => (
          <button
            key={p.v}
            data-testid={`period-${p.v}`}
            onClick={() => choosePreset(p.v)}
            className={`h-9 px-4 border text-xs font-bold tracking-widest uppercase transition-colors ${
              preset === p.v ? "bg-amber text-ink border-amber" : "bg-transparent text-cream/70 border-amber/30 hover:text-amber"
            }`}
          >
            {p.l}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-end gap-3">
            <label className="block">
              <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">Fra</span>
              <input type="date" data-testid="analytics-date-from" value={range.date_from}
                onChange={(e) => setRange((p) => ({ ...p, date_from: e.target.value }))}
                className="h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2" />
            </label>
            <label className="block">
              <span className="block text-[10px] font-bold tracking-widest uppercase text-amber/70 mb-1.5">Til</span>
              <input type="date" data-testid="analytics-date-to" value={range.date_to}
                onChange={(e) => setRange((p) => ({ ...p, date_to: e.target.value }))}
                className="h-9 bg-ink border border-amber/25 focus:border-amber text-sm text-cream px-2" />
            </label>
          </div>
        )}
      </div>

      {/* Core metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-metrics">
        <Metric testId="metric-visitors" label="Unike besøkende" value={t?.unique_visitors ?? 0} sub={`${t?.sessions ?? 0} sesjoner`} />
        <Metric testId="metric-pageviews" label="Sidevisninger" value={t?.page_views ?? 0} color="text-cream" />
        <Metric testId="metric-cta" label="CTA-klikk" value={t?.cta_clicks ?? 0} sub={`${r?.visitor_to_cta ?? 0}% av besøkende`} />
        <Metric testId="metric-starts" label="Startet registrering" value={t?.registration_starts ?? 0} color="text-cream" />
        <Metric testId="metric-registrations" label="Registreringer" value={t?.registrations ?? 0} color="text-orange" />
        <Metric testId="metric-conv-visitor" label="Besøkende → registrering" value={`${r?.visitor_to_registration ?? 0}%`} />
        <Metric testId="metric-conv-start" label="Start → fullført" value={`${r?.start_to_completion ?? 0}%`} />
        <Metric testId="metric-conv-cta" label="CTA → start" value={`${r?.cta_to_start ?? 0}%`} color="text-cream" />
      </div>

      {data && <div className="mb-8"><Funnel funnel={data.funnel} /></div>}

      {/* Filters */}
      <div className="bg-amber-deep/40 border border-amber/15 p-4 md:p-5 mb-8" data-testid="analytics-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Select testId="filter-source-type" label="Trafikktype" value={filters.source_type} onChange={setF("source_type")}
            options={opt("Alle", options?.source_types)} />
          <Select testId="filter-utm-source" label="UTM source" value={filters.utm_source} onChange={setF("utm_source")}
            options={opt("Alle", options?.utm_sources)} />
          <Select testId="filter-utm-medium" label="UTM medium" value={filters.utm_medium} onChange={setF("utm_medium")}
            options={opt("Alle", options?.utm_mediums)} />
          <Select testId="filter-utm-campaign" label="Kampanje" value={filters.utm_campaign} onChange={setF("utm_campaign")}
            options={opt("Alle", options?.utm_campaigns)} />
          <Select testId="filter-device" label="Enhet" value={filters.device} onChange={setF("device")}
            options={opt("Alle", options?.devices)} />
          <Select testId="filter-page" label="Side" value={filters.page} onChange={setF("page")}
            options={opt("Alle", options?.pages)} />
          <Select testId="filter-journey" label="Reise" value={filters.journey} onChange={setF("journey")}
            options={[{ v: "", l: "Alle" }, { v: "huseier", l: "Huseier" }, { v: "pendler", l: "Pendler" }]} />
        </div>
        <button
          onClick={() => setFilters({ source_type: "", utm_source: "", utm_medium: "", utm_campaign: "", device: "", page: "", journey: "" })}
          data-testid="analytics-filter-reset"
          className="mt-3 h-9 px-4 border border-amber/30 text-cream/70 hover:text-amber text-xs font-bold tracking-widest uppercase"
        >
          Nullstill filtre
        </button>
      </div>

      {error && <div className="bg-orange/10 border border-orange p-4 text-orange mb-6 text-sm" data-testid="analytics-error">{error}</div>}
      {loading && <p className="text-cream/50 text-sm mb-6" data-testid="analytics-loading">Laster...</p>}

      <div className={loading ? "opacity-50 transition-opacity" : "transition-opacity"}>
      {/* Daily trend */}
      <div className="bg-amber-deep/30 border border-amber/15 p-5 mb-8" data-testid="analytics-daily">
        <h2 className="font-display text-lg font-bold tracking-tight mb-4">Daglig utvikling</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.daily || []} margin={{ top: 5, right: 10, bottom: 5, left: -18 }}>
              <CartesianGrid stroke="rgba(232,168,0,0.12)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "rgba(245,240,232,0.6)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "rgba(245,240,232,0.6)", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0D0D0D", border: "1px solid rgba(232,168,0,0.4)", color: "#F5F0E8" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="visitors" name="Besøkende" fill="#E8A800" />
              <Bar dataKey="cta_clicks" name="CTA-klikk" fill="#8a6400" />
              <Bar dataKey="registrations" name="Registreringer" fill="#FF6B00" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Breakdown tables */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Table testId="analytics-sources" title="Trafikkilder" keyLabel="Kilde" rows={data?.sources || []} columns="short" />
        <Table testId="analytics-campaigns" title="Kampanjer" keyLabel="UTM campaign" rows={data?.campaigns || []} />
        <Table testId="analytics-mediums" title="UTM medium" keyLabel="Medium" rows={data?.mediums || []} columns="short" />
        <Table testId="analytics-journeys" title="Huseier vs pendler" keyLabel="Reise" rows={data?.journeys || []} labels={JOURNEY_LABELS} />
        <Table testId="analytics-devices" title="Enheter" keyLabel="Enhet" rows={data?.devices || []} labels={DEVICE_LABELS} columns="short" />
        <div className="border border-amber/15 overflow-x-auto" data-testid="analytics-pages">
          <div className="px-4 py-3 bg-amber-deep/60">
            <h3 className="font-display text-base font-bold tracking-tight">Sider</h3>
          </div>
          <table className="w-full text-sm min-w-[420px]">
            <thead className="text-amber/80 text-xs font-bold tracking-widest uppercase border-b border-amber/15">
              <tr>
                <th className="text-left px-4 py-2.5">Side</th>
                <th className="text-right px-4 py-2.5">Visninger</th>
                <th className="text-right px-4 py-2.5">Unike</th>
                <th className="text-right px-4 py-2.5">Snitt</th>
              </tr>
            </thead>
            <tbody>
              {(data?.pages || []).length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-cream/50">Ingen data i perioden.</td></tr>
              )}
              {(data?.pages || []).map((p) => (
                <tr key={p.page} className="border-t border-amber/10 hover:bg-amber/5">
                  <td className="px-4 py-2.5 text-cream">{PAGE_LABELS[p.page] || p.page}</td>
                  <td className="px-4 py-2.5 text-right text-amber font-bold">{p.views}</td>
                  <td className="px-4 py-2.5 text-right text-cream/80">{p.unique_visitors}</td>
                  <td className="px-4 py-2.5 text-right text-cream/60">{p.views_per_visitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>

      <p className="text-xs text-cream/40">
        Anonyme førsteparts-data: tilfeldig besøkende-/sesjons-ID i nettleseren, ingen IP-lagring, ingen fingerprinting.
        Registreringer telles én gang per opprettet lead og stemmer med Leads-fanen.
      </p>
    </div>
  );
}
